import { Action } from 'redux';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { DataAcceleration } from '~src/bootstrap/redux/actions/AppActionTypes';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { AppState } from '~src/bootstrap/redux/state/AppState';
import { Messages } from '~src/codegen/Localize';
import { EntityActionNames, IEntityAction } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/IEntityAction';
import { anyItemIsUnpublishedWorkbook } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/IEntityActionHelpers';
import { IDataAccelerationActions } from '~src/spacesPlatform/models/actionNames';
import { ViewContentType, WorkbookContentType } from '~src/spacesPlatform/models/ContentTypes';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { selectSpaceInfoItem } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { isWorkbookSpace } from '~src/spacesPlatform/state/SpaceInfo';
import { currentUserIsAdmin, currentUserIsOwner } from '~src/spacesPlatform/utils/MixedContentHelpers';

import { ViewDataAccelerationState } from '../types/IViewAcceleration';


export const DataAccelerationOnEntityAction: IEntityAction<IContentInfo, IDataAccelerationActions> = {
  name: EntityActionNames.DataAccelerationOn,
  translateName: (targetItems: IContentInfo[]) => {
    if (singleViewSelected(targetItems)) {
      return Messages.dataAccelerationOn_action();
    } else if (singleWorkbookSelected(targetItems)) {
      return Messages.dataAccelerationOnForAllViews_action();
    } else {
      // for multiple views
      return Messages.dataAccelerationOnForSelectedViews_action();
    }
  },
  isVisible: IsVisible,
  // this property contorls whether the action is clickable
  isClickable: (availableActions: IDataAccelerationActions, targetItems: IContentInfo[]) => {
    if (anyItemIsUnpublishedWorkbook(targetItems)) {
      return false;
    }

    return targetItems.length > 1 || (availableActions && availableActions.enableAcceleration);
  },
  // this property controls whether to add a check mark (indicating the current selection) in the button
  isChecked: (availableActions: IDataAccelerationActions, targetItems: IContentInfo[], state: AppState) => {
    if (targetItems.length === 1 && targetItems[0].contentType === ViewContentType.name){
      return targetItems[0].acceleration.accelerationState !== ViewDataAccelerationState.Off;
    }
    return false;
  },
  createReduxAction: (targetItems: IContentInfo[]) => dataAccelerationForContents(targetItems, DataAccelerationType.turnOnAcceleration)
};

export const DataAccelerationOffEntityAction: IEntityAction<IContentInfo, IDataAccelerationActions> = {
  name: EntityActionNames.DataAccelerationOff,
  translateName: (targetItems: IContentInfo[]) => {
    if (singleViewSelected(targetItems)) {
      return Messages.dataAccelerationOff_action();
    } else if (singleWorkbookSelected(targetItems)) {
        return Messages.dataAccelerationOffForAllViews_action();
    } else {
      // for multiple views
      return Messages.dataAccelerationOffForSelectedViews_action();
    }
  },
  isVisible: IsVisible,
  isClickable: (availableActions: IDataAccelerationActions, targetItems: IContentInfo[]) => {
    if (anyItemIsUnpublishedWorkbook(targetItems)) {
      return false;
    }

    return targetItems.length > 1 || (availableActions && availableActions.disableAcceleration);
  },
  isChecked: (availableActions: IDataAccelerationActions, targetItems: IContentInfo[], state: AppState) => {
    if (targetItems.length === 1 && targetItems[0].contentType === ViewContentType.name){
      return targetItems[0].acceleration.accelerationState === ViewDataAccelerationState.Off;
    }
    return false;
  },
  createReduxAction: (targetItems: IContentInfo[]) => dataAccelerationForContents(targetItems, DataAccelerationType.turnOffAcceleration)
};

function singleViewSelected(targetItems: IContentInfo[]) {
  return targetItems.length === 1 && targetItems[0].contentType === ViewContentType.name;
}

function singleWorkbookSelected(targetItems: IContentInfo[]) {
  return targetItems.length === 1 && targetItems[0].contentType === WorkbookContentType.name;
}

export const DataAccelerationSubmenu: IEntityAction<IContentInfo, unknown> = {
  name: EntityActionNames.DataAccelerationSubmenu,
  translateName: Messages.dataAcceleration_action_submenu,
  isVisible: IsVisible,
  isClickable: (availableActions: IDataAccelerationActions, targetItems: IContentInfo[], state: AppState) => {
    if (anyItemIsUnpublishedWorkbook(targetItems)) {
      return false;
    }

    if (targetItems.length === 1 && targetItems[0].contentType === ViewContentType.name) {
      return (availableActions && availableActions.enableAcceleration) || targetItems[0].acceleration.accelerationState !== ViewDataAccelerationState.Off;
    }
    // workbook and multiple view cases
    return true;
  },
  createReduxAction: () => null,
};

export enum DataAccelerationType {
  turnOnAcceleration = 'turnOnAcceleration',
  turnOffAcceleration = 'turnOffAcceleration'
}

export interface DataAccelerationAction extends Action {
  selectedContent: IContentInfo[];
  dataAccelerationType: DataAccelerationType;
}

export function dataAccelerationForContents(selectedContent: IContentInfo[], dataAccelerationType: DataAccelerationType): DataAccelerationAction {
  return {
    type: DataAcceleration,
    selectedContent,
    dataAccelerationType,
  };
}

function IsVisible(targetItems: IContentInfo[], state: AppState): boolean {
  const spaceItem = selectSpaceInfoItem(state);
  return FeatureFlags.workbookAccelerationUIEnabled && isWorkbookSpace(state.spaceInfo) && userCanAccelerate(state, spaceItem);
}

function userCanAccelerate(state: AppState, spaceItem: any): boolean {
  return currentUserIsAdmin(state) || (SessionSiteSelectors.canOwnerAccelerate(state) && currentUserIsOwner(state, spaceItem));
}
